import { AnimatedSprite, Container, Sprite, Texture, TilingSprite } from 'pixi.js';
import { CircularProgressBar } from '@pixi/ui';
import type { ContainerModel, SpriteModel, TextModel } from './populateLayouts/types/models';
import { ModelType } from './populateLayouts/types/models';
import { RuntimeType } from './populateLayouts/types/RuntimeType';
import { LayoutText } from './populateLayouts/LayoutText';
import { getLocalizedString } from '../../app/localization/getLocalizedString';
import { LayoutHTMLText } from './populateLayouts/LayoutHTMLText';

export interface PopulateRunTimeFactories {
  [ModelType.Container]: Partial<Record<RuntimeType, (model: ContainerModel) => Container | CircularProgressBar>>;
  [ModelType.Sprite]: Partial<Record<RuntimeType, (model: SpriteModel) => Sprite | TilingSprite>>;
  [ModelType.Text]: Partial<Record<RuntimeType, (model: TextModel) => LayoutText | LayoutHTMLText>>;
}

interface PopulateConfig {
  runTimeFactories: PopulateRunTimeFactories;
  getScreenSize: () => { width: number; height: number };
}

const config: PopulateConfig = {
  runTimeFactories: {
    [ModelType.Container]: {
      [RuntimeType.CircularProgressBar]: (model: ContainerModel) => {
        const data = {
          backgroundColor: 0xffffff,
          backgroundAlpha: 0.5,
          lineWidth: 10,
          fillColor: 0xff0000,
          radius: 50,
          fillAlpha: 1,
          value: 50,
          cap: 'round',
          ...(model.data ? (model.data as { constructorParams: object }).constructorParams ?? {} : {}),
        };

        return new CircularProgressBar({
          backgroundAlpha: data.backgroundAlpha,
          cap: data.cap as 'butt' | 'round' | 'square',
          fillAlpha: data.fillAlpha,
          fillColor: data.fillColor,
          lineWidth: data.lineWidth,
          radius: data.radius,
          value: data.value,
          backgroundColor: data.backgroundColor,
        });
      },
    },
    [ModelType.Text]: {},
    [ModelType.Sprite]: {
      [RuntimeType.AnimatedSprite]: (model) => new AnimatedSprite([Texture.from(model.texture)]),
      [RuntimeType.TilingSprite]: (model) => new TilingSprite(Texture.from(model.texture)),
    },
  },
  getScreenSize: (): { width: number; height: number } => ({ width: 780, height: 1280 }),
};

export function getContainerInstance(model: ContainerModel): Container {
  if (!model.runtimeType) return new Container();
  const factory = config.runTimeFactories?.[ModelType.Container]?.[model.runtimeType];
  return factory ? factory(model) : new Container();
}

export function getSpriteInstance(model: SpriteModel): Sprite | TilingSprite {
  if (!model.runtimeType) return new Sprite();
  const factory = config.runTimeFactories?.[ModelType.Sprite]?.[model.runtimeType];
  return factory ? factory(model) : new Sprite();
}

export function getTextInstance(model: TextModel): LayoutText | LayoutHTMLText {
  if ((model.data as { colorTags?: Record<string, string> })?.colorTags) {
    return new LayoutHTMLText();
  }

  if (!model.runtimeType) return new LayoutText();
  const factory = config.runTimeFactories?.[ModelType.Text]?.[model.runtimeType];
  return factory ? factory(model) : new LayoutText();
}

export function getLocalized(stringKey: string): string {
  return getLocalizedString(stringKey);
}

export function getScreenSize(): { width: number; height: number } {
  return config.getScreenSize();
}

export function initPopulate(newConfig: PopulateConfig): void {
  Object.assign(config.runTimeFactories[ModelType.Container], newConfig.runTimeFactories[ModelType.Container]);
  Object.assign(config.runTimeFactories[ModelType.Sprite], newConfig.runTimeFactories[ModelType.Sprite]);
  Object.assign(config.runTimeFactories[ModelType.Text], newConfig.runTimeFactories[ModelType.Text]);
  config.getScreenSize = newConfig.getScreenSize;
}
